import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Button,
  Space,
  Typography,
  List,
  Tag,
  Timeline,
  Statistic,
  Alert,
  message,
  Spin,
} from 'antd';
import {
  FileTextOutlined,
  CarOutlined,
  DollarOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  HistoryOutlined,
  CreditCardOutlined,
  ShoppingOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { getCustomerPortalStats, CustomerPortalStats } from '../../services/statsApi';
import TemplateGallery from '../CustomerPortal/TemplateGallery';
import QuoteRequestForm from '../CustomerPortal/QuoteRequestForm';

const { Title, Text } = Typography;

const CustomerPortalDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templateGalleryVisible, setTemplateGalleryVisible] = useState(false);
  const [quoteRequestVisible, setQuoteRequestVisible] = useState(false);
  const [stats, setStats] = useState<CustomerPortalStats | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    setLoading(true);
    try {
      const data = await getCustomerPortalStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
      message.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      sent: 'processing',
      approved: 'success',
      declined: 'error',
      completed: 'success',
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" tip="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <div>
          <Title level={2}>Customer Portal</Title>
          <Text type="secondary">
            Welcome back, {user?.first_name}! Manage your vehicles and service history.
          </Text>
        </div>

        {/* Pending Actions Alert */}
        {(stats?.pendingEstimates ?? 0) > 0 && (
          <Alert
            message="You have pending estimates"
            description="Please review and approve your estimates to schedule service."
            type="info"
            showIcon
            action={
              <Button size="small" onClick={() => navigate('/portal/estimates')}>
                View Estimates
              </Button>
            }
          />
        )}

        {/* Quick Stats */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Pending Estimates"
                value={stats?.pendingEstimates || 0}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="My Vehicles"
                value={stats?.myVehicles.length || 0}
                prefix={<CarOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Service History"
                value={stats?.serviceHistory.length || 0}
                prefix={<HistoryOutlined />}
                suffix="services"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Spent YTD"
                value={(stats?.totalSpentYTD || 0).toFixed(2)}
                prefix="$"
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Quick Actions */}
        <Card title="Quick Actions">
          <Space wrap>
            <Button
              type="primary"
              icon={<ShoppingOutlined />}
              onClick={() => setTemplateGalleryVisible(true)}
            >
              Browse Service Packages
            </Button>
            <Button
              icon={<MessageOutlined />}
              onClick={() => setQuoteRequestVisible(true)}
            >
              Request Custom Quote
            </Button>
            <Button
              icon={<CalendarOutlined />}
              onClick={() => navigate('/portal/appointments')}
            >
              Schedule Appointment
            </Button>
            <Button
              icon={<FileTextOutlined />}
              onClick={() => navigate('/portal/estimates')}
            >
              View Estimates
            </Button>
            <Button
              icon={<CreditCardOutlined />}
              onClick={() => navigate('/portal/payments')}
            >
              Make Payment
            </Button>
            <Button
              icon={<CarOutlined />}
              onClick={() => navigate('/portal/vehicles')}
            >
              Manage Vehicles
            </Button>
          </Space>
        </Card>

        {/* Main Content */}
        <Row gutter={[16, 16]}>
          {/* Pending Estimates */}
          <Col xs={24} lg={12}>
            <Card
              title={
                <Space>
                  <FileTextOutlined />
                  <span>Pending Estimates</span>
                </Space>
              }
              extra={
                <Button
                  type="link"
                  onClick={() => navigate('/portal/estimates')}
                >
                  View All
                </Button>
              }
            >
              {(stats?.pendingEstimates ?? 0) > 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <FileTextOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
                  <div>
                    <Text strong style={{ fontSize: '18px' }}>{stats?.pendingEstimates} Pending Estimate{(stats?.pendingEstimates ?? 0) > 1 ? 's' : ''}</Text>
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <Button type="primary" onClick={() => navigate('/portal/estimates')}>
                      View Estimates
                    </Button>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <CheckCircleOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                  <div>
                    <Text type="secondary">No pending estimates</Text>
                  </div>
                </div>
              )}
            </Card>
          </Col>

          {/* My Vehicles */}
          <Col xs={24} lg={12}>
            <Card
              title={
                <Space>
                  <CarOutlined />
                  <span>My Vehicles</span>
                </Space>
              }
              extra={
                <Button
                  type="link"
                  onClick={() => navigate('/portal/vehicles')}
                >
                  Manage
                </Button>
              }
            >
              {(stats?.myVehicles || []).length > 0 ? (
                <List
                  dataSource={stats?.myVehicles || []}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        title={`${item.year} ${item.make} ${item.model}`}
                        description={
                          <Space direction="vertical" size={0}>
                            <Text type="secondary">VIN: {item.vin}</Text>
                            <Text type="secondary">License: {item.license_plate}</Text>
                            {item.current_mileage && (
                              <Text type="secondary">Mileage: {item.current_mileage.toLocaleString()} miles</Text>
                            )}
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Text type="secondary">No vehicles registered</Text>
                </div>
              )}
            </Card>
          </Col>
        </Row>

        {/* Service History & Reminders */}
        <Row gutter={[16, 16]}>
          {/* Service History */}
          <Col xs={24} lg={14}>
            <Card
              title={
                <Space>
                  <HistoryOutlined />
                  <span>Service History</span>
                </Space>
              }
              extra={
                <Button
                  type="link"
                  onClick={() => navigate('/portal/history')}
                >
                  View All
                </Button>
              }
            >
              {(stats?.serviceHistory || []).length > 0 ? (
                <Timeline
                  items={(stats?.serviceHistory || []).map((item) => ({
                    color: 'green',
                    children: (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <Text strong>{item.service_type} - {item.description}</Text>
                          <Text strong style={{ color: '#52c41a' }}>${item.total_amount.toFixed(2)}</Text>
                        </div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {new Date(item.date).toLocaleDateString()}
                        </Text>
                      </div>
                    ),
                  }))}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Text type="secondary">No service history available</Text>
                </div>
              )}
            </Card>
          </Col>

          {/* Upcoming Reminders */}
          <Col xs={24} lg={10}>
            <Card
              title={
                <Space>
                  <ClockCircleOutlined />
                  <span>Upcoming Service Reminders</span>
                </Space>
              }
            >
              {(stats?.upcomingReminders || []).length > 0 ? (
                <List
                  dataSource={stats?.upcomingReminders || []}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        title={<Text strong>{item.service_type}</Text>}
                        description={
                          <Space direction="vertical" size={0}>
                            <Text type="secondary">Vehicle: {item.vehicle}</Text>
                            <Text type="secondary">Due: {new Date(item.due_date).toLocaleDateString()}</Text>
                            {item.due_mileage && (
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {item.due_mileage.toLocaleString()} miles
                              </Text>
                            )}
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Text type="secondary">No upcoming reminders</Text>
                </div>
              )}
              <div style={{ marginTop: 16 }}>
                <Button
                  type="primary"
                  block
                  icon={<CalendarOutlined />}
                  onClick={() => navigate('/portal/appointments')}
                >
                  Schedule Service
                </Button>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Contact Information */}
        <Card title="Need Help?">
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <div>
                <Text type="secondary">Phone</Text>
                <div><Text strong>555-123-4567</Text></div>
              </div>
            </Col>
            <Col span={8}>
              <div>
                <Text type="secondary">Email</Text>
                <div><Text strong>service@cnfautosuite.com</Text></div>
              </div>
            </Col>
            <Col span={8}>
              <div>
                <Text type="secondary">Hours</Text>
                <div><Text strong>Mon-Fri: 8AM - 6PM</Text></div>
              </div>
            </Col>
          </Row>
        </Card>
      </Space>

      {/* Template Gallery Modal */}
      <TemplateGallery
        visible={templateGalleryVisible}
        customerId={user?.id}
        onClose={() => setTemplateGalleryVisible(false)}
      />

      {/* Quote Request Form Modal */}
      <QuoteRequestForm
        visible={quoteRequestVisible}
        customerId={user?.id}
        onClose={() => setQuoteRequestVisible(false)}
        onSuccess={() => {
          // Optionally refresh data or show a success message
        }}
      />
    </div>
  );
};

export default CustomerPortalDashboard;
